(() => {
  "use strict";

  if (window.__soapBubbleRevealLoaded) return;
  window.__soapBubbleRevealLoaded = true;

  const instances = new Map();
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const random = (min, max) => Math.random() * (max - min) + min;
  const randomInt = (min, max) => Math.floor(random(min, max + 1));

  class SoapBubbleReveal {
    constructor(section) {
      this.section = section;
      this.layer = section.querySelector("[data-soap-bubble-layer]");
      this.product = section.querySelector("[data-soap-bubble-product]");
      this.visual = section.querySelector("[data-soap-bubble-visual]");

      this.animationEnabled = section.dataset.animationEnabled === "true";
      this.desktopCount = Number(section.dataset.bubbleCountDesktop || 24);
      this.mobileCount = Number(section.dataset.bubbleCountMobile || 10);
      this.baseDuration = Number(section.dataset.animationDuration || 2200);
      this.revealDelay = Number(section.dataset.productRevealDelay || 1900);
      this.floatEnabled = section.dataset.productFloating === "true";

      this.motionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.reducedMotion = this.motionMedia.matches;

      this.bubbles = [];
      this.observer = null;
      this.resizeTimer = null;
      this.revealTimer = null;
      this.completeTimer = null;
      this.hasPlayed = false;
      this.isAnimating = false;
      this.destroyed = false;

      this.handleResize = this.handleResize.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);

      this.init();
    }

    init() {
      if (!this.section || !this.product || !this.layer) return;

      if (this.floatEnabled) {
        this.section.classList.add("soap-bubble-reveal--floating");
      }

      window.addEventListener("resize", this.handleResize, { passive: true });

      if (typeof this.motionMedia.addEventListener === "function") {
        this.motionMedia.addEventListener("change", this.handleMotionChange);
      } else if (typeof this.motionMedia.addListener === "function") {
        this.motionMedia.addListener(this.handleMotionChange);
      }

      if (!this.animationEnabled || this.reducedMotion) {
        this.showStatic();
        return;
      }

      this.setupObserver();
    }

    setupObserver() {
      if (!("IntersectionObserver" in window)) {
        this.play();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (
              entry.target === this.section &&
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.22
            ) {
              this.play();
              this.disconnectObserver();
              break;
            }
          }
        },
        {
          threshold: [0, 0.22, 0.3, 0.5]
        }
      );

      this.observer.observe(this.section);
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    getBubbleCount() {
      const width = window.innerWidth;

      if (width <= 749) {
        return clamp(this.mobileCount, 8, 12);
      }

      if (width <= 1100) {
        const tabletCount = Math.round(
          (this.desktopCount + this.mobileCount) / 2
        );
        return clamp(tabletCount, 12, 18);
      }

      return clamp(this.desktopCount, 20, 30);
    }

    getSectionMetrics() {
      const sectionRect = this.section.getBoundingClientRect();
      const productRect = this.product.getBoundingClientRect();

      return {
        sectionRect,
        width: sectionRect.width,
        height: sectionRect.height,
        productCenterX:
          productRect.left - sectionRect.left + productRect.width / 2,
        productCenterY:
          productRect.top - sectionRect.top + productRect.height / 2,
        productWidth: productRect.width,
        productHeight: productRect.height
      };
    }

    createStartPoint(index, count, metrics) {
      const { width, height, productCenterX, productCenterY } = metrics;

      /*
        Nine broad origin zones guarantee whole-section coverage rather
        than generating bubbles only around the product.
      */
      const zones = [
        [0.03, 0.28, 0.02, 0.22], // top-left
        [0.32, 0.68, 0.01, 0.20], // top-center
        [0.72, 0.98, 0.02, 0.24], // top-right
        [0.02, 0.28, 0.30, 0.64], // middle-left
        [0.34, 0.65, 0.28, 0.66], // middle-center
        [0.02, 0.30, 0.74, 0.98], // bottom-left
        [0.32, 0.68, 0.78, 0.99], // bottom-center
        [0.72, 0.98, 0.72, 0.98], // bottom-right
        [0.02, 0.98, 0.04, 0.96]  // fully random
      ];

      const zone = zones[index % zones.length];
      const minimumDistance =
        Math.min(width, height) * (window.innerWidth <= 749 ? 0.18 : 0.22);

      let x = width * random(zone[0], zone[1]);
      let y = height * random(zone[2], zone[3]);

      for (let attempt = 0; attempt < 8; attempt += 1) {
        const distance = Math.hypot(
          x - productCenterX,
          y - productCenterY
        );

        if (distance >= minimumDistance) break;

        x = width * random(zone[0], zone[1]);
        y = height * random(zone[2], zone[3]);
      }

      /*
        A few bubbles begin partially outside the section for a more
        organic premium composition.
      */
      if (index % 7 === 0) {
        x += index % 14 === 0 ? -random(8, 30) : random(8, 30);
      }

      if (index % 11 === 0) {
        y += index % 22 === 0 ? -random(8, 24) : random(8, 24);
      }

      return {
        nx: x / Math.max(width, 1),
        ny: y / Math.max(height, 1)
      };
    }

    buildBubbles() {
      this.clearBubbles();

      const count = this.getBubbleCount();
      const metrics = this.getSectionMetrics();
      const isMobile = window.innerWidth <= 749;

      for (let i = 0; i < count; i += 1) {
        const bubble = document.createElement("span");
        bubble.className = "soap-bubble-reveal__bubble";
        bubble.setAttribute("aria-hidden", "true");

        const start = this.createStartPoint(i, count, metrics);
        const size = isMobile
          ? random(14, 64)
          : random(18, 92);

        const opacityMultiplier = random(0.78, 1);
        const startScale = random(0.72, 1.08);
        const endScale = random(0.58, 0.95);
        const startRotation = randomInt(-55, 55);
        const endRotation =
          startRotation + randomInt(-70, 90);

        const delay = Math.round(random(40, isMobile ? 280 : 390));

        /*
          Durations vary around the merchant-selected base duration
          while still keeping the complete reveal under ~3 seconds.
        */
        const duration = Math.round(
          clamp(
            this.baseDuration + random(-320, 220),
            1350,
            2500
          )
        );

        bubble.style.setProperty(
          "--soap-bubble-size",
          `${size.toFixed(1)}px`
        );
        bubble.style.setProperty(
          "--soap-bubble-opacity",
          (
            parseFloat(
              getComputedStyle(this.section).getPropertyValue(
                "--soap-bubble-reveal-bubble-opacity"
              )
            ) || 0.75
          ).toFixed(3)
        );
        bubble.style.setProperty(
          "--soap-bubble-visual-opacity",
          opacityMultiplier.toFixed(3)
        );
        bubble.style.setProperty(
          "--soap-bubble-start-scale",
          startScale.toFixed(3)
        );
        bubble.style.setProperty(
          "--soap-bubble-end-scale",
          endScale.toFixed(3)
        );
        bubble.style.setProperty(
          "--soap-bubble-start-rotation",
          `${startRotation}deg`
        );
        bubble.style.setProperty(
          "--soap-bubble-end-rotation",
          `${endRotation}deg`
        );
        bubble.style.setProperty(
          "--soap-bubble-delay",
          `${delay}ms`
        );
        bubble.style.setProperty(
          "--soap-bubble-duration",
          `${duration}ms`
        );

        const targetRadiusX = Math.max(
          26,
          Math.min(metrics.productWidth * 0.28, 100)
        );
        const targetRadiusY = Math.max(
          32,
          Math.min(metrics.productHeight * 0.24, 120)
        );

        const targetFactorX = random(-1, 1);
        const targetFactorY = random(-1, 1);

        const record = {
          element: bubble,
          startNX: start.nx,
          startNY: start.ny,
          delay,
          duration,
          targetFactorX,
          targetFactorY,
          targetRadiusX,
          targetRadiusY,
          endScale
        };

        this.bubbles.push(record);
        this.layer.appendChild(bubble);
      }

      this.updateBubbleGeometry();
    }

    updateBubbleGeometry() {
      if (!this.bubbles.length) return;

      const metrics = this.getSectionMetrics();
      const {
        width,
        height,
        productCenterX,
        productCenterY,
        productWidth,
        productHeight
      } = metrics;

      const targetRadiusX = Math.max(
        24,
        Math.min(productWidth * 0.28, 105)
      );
      const targetRadiusY = Math.max(
        28,
        Math.min(productHeight * 0.24, 120)
      );

      this.bubbles.forEach((record) => {
        const bubble = record.element;
        const startX = record.startNX * width;
        const startY = record.startNY * height;

        const targetX =
          productCenterX + record.targetFactorX * targetRadiusX;
        const targetY =
          productCenterY + record.targetFactorY * targetRadiusY;

        const dx = targetX - startX;
        const dy = targetY - startY;

        bubble.style.left = `${startX.toFixed(2)}px`;
        bubble.style.top = `${startY.toFixed(2)}px`;
        bubble.style.setProperty(
          "--soap-bubble-dx",
          `${dx.toFixed(2)}px`
        );
        bubble.style.setProperty(
          "--soap-bubble-dy",
          `${dy.toFixed(2)}px`
        );

        const driftX = random(-14, 14);
        const driftY = random(-18, 10);

        bubble.style.setProperty(
          "--soap-bubble-drift-x",
          `${driftX.toFixed(1)}px`
        );
        bubble.style.setProperty(
          "--soap-bubble-drift-y",
          `${driftY.toFixed(1)}px`
        );
        bubble.style.setProperty(
          "--soap-bubble-ambient-duration",
          `${random(3.8, 6.2).toFixed(2)}s`
        );
        bubble.style.setProperty(
          "--soap-bubble-ambient-delay",
          `${random(-2.5, 0).toFixed(2)}s`
        );
      });
    }

    play() {
      if (
        this.destroyed ||
        this.hasPlayed ||
        this.isAnimating ||
        !this.animationEnabled
      ) {
        return;
      }

      if (this.motionMedia.matches) {
        this.showStatic();
        return;
      }

      this.isAnimating = true;
      this.hasPlayed = true;
      this.section.classList.remove(
        "soap-bubble-reveal--revealed",
        "soap-bubble-reveal--reveal-complete"
      );

      this.buildBubbles();

      /*
        Two lightweight animation frames are only used to ensure the
        browser commits initial bubble positions before transitions start.
        There is no continuous requestAnimationFrame loop.
      */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.destroyed) return;

          this.bubbles.forEach(({ element }) => {
            element.classList.add(
              "soap-bubble-reveal__bubble--converging"
            );
          });
        });
      });

      this.revealTimer = window.setTimeout(() => {
        if (this.destroyed) return;
        this.section.classList.add(
          "soap-bubble-reveal--revealed"
        );
      }, clamp(this.revealDelay, 1200, 2400));

      const convergenceEnd = this.bubbles.reduce(
        (latest, record) =>
          Math.max(latest, record.delay + record.duration),
        0
      );

      /*
        Cap the post-convergence handoff so the visual payoff remains fast.
      */
      const completeAt = Math.min(
        Math.max(convergenceEnd + 80, this.revealDelay + 650),
        3000
      );

      this.completeTimer = window.setTimeout(() => {
        if (this.destroyed) return;
        this.finishReveal();
      }, completeAt);
    }

    finishReveal() {
      this.section.classList.add(
        "soap-bubble-reveal--revealed",
        "soap-bubble-reveal--reveal-complete"
      );

      const isMobile = window.innerWidth <= 749;
      const survivorCount = isMobile
        ? Math.min(4, this.bubbles.length)
        : Math.min(7, this.bubbles.length);

      const survivorIndexes = new Set();

      while (
        survivorIndexes.size < survivorCount &&
        survivorIndexes.size < this.bubbles.length
      ) {
        survivorIndexes.add(
          Math.floor(Math.random() * this.bubbles.length)
        );
      }

      this.bubbles.forEach((record, index) => {
        const bubble = record.element;

        if (survivorIndexes.has(index)) {
          bubble.classList.remove(
            "soap-bubble-reveal__bubble--converging"
          );
          bubble.classList.add(
            "soap-bubble-reveal__bubble--ambient"
          );
        } else {
          bubble.classList.remove(
            "soap-bubble-reveal__bubble--converging"
          );
          bubble.classList.add(
            "soap-bubble-reveal__bubble--vanish"
          );
        }
      });

      this.isAnimating = false;

      /*
        Remove fully vanished bubbles from the DOM after their short fade.
        Ambient survivors remain extremely lightweight.
      */
      window.setTimeout(() => {
        if (this.destroyed) return;

        this.bubbles = this.bubbles.filter((record) => {
          if (
            record.element.classList.contains(
              "soap-bubble-reveal__bubble--vanish"
            )
          ) {
            record.element.remove();
            return false;
          }

          return true;
        });
      }, 520);
    }

    showStatic() {
      this.disconnectObserver();
      this.clearTimers();
      this.clearBubbles();

      this.isAnimating = false;
      this.hasPlayed = true;

      this.section.classList.add(
        "soap-bubble-reveal--revealed"
      );

      if (!this.reducedMotion && this.floatEnabled) {
        this.section.classList.add(
          "soap-bubble-reveal--reveal-complete"
        );
      } else {
        this.section.classList.remove(
          "soap-bubble-reveal--reveal-complete"
        );
      }
    }

    handleResize() {
      if (this.destroyed) return;

      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        if (this.destroyed) return;

        /*
          Product center and all bubble targets are recalculated from the
          actual responsive product position. No fixed destination pixels.
        */
        this.updateBubbleGeometry();
      }, 120);
    }

    handleMotionChange(event) {
      this.reducedMotion = event.matches;

      if (this.reducedMotion) {
        this.showStatic();
      }
    }

    clearTimers() {
      window.clearTimeout(this.revealTimer);
      window.clearTimeout(this.completeTimer);
      window.clearTimeout(this.resizeTimer);

      this.revealTimer = null;
      this.completeTimer = null;
      this.resizeTimer = null;
    }

    clearBubbles() {
      this.bubbles.forEach(({ element }) => element.remove());
      this.bubbles = [];

      if (this.layer) {
        this.layer.textContent = "";
      }
    }

    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;
      this.disconnectObserver();
      this.clearTimers();
      this.clearBubbles();

      window.removeEventListener("resize", this.handleResize);

      if (typeof this.motionMedia.removeEventListener === "function") {
        this.motionMedia.removeEventListener(
          "change",
          this.handleMotionChange
        );
      } else if (typeof this.motionMedia.removeListener === "function") {
        this.motionMedia.removeListener(
          this.handleMotionChange
        );
      }
    }
  }

  const initSection = (section) => {
    if (
      !section ||
      !section.matches("[data-soap-bubble-reveal]") ||
      instances.has(section)
    ) {
      return;
    }

    instances.set(section, new SoapBubbleReveal(section));
  };

  const initAll = (root = document) => {
    root
      .querySelectorAll("[data-soap-bubble-reveal]")
      .forEach(initSection);
  };

  const boot = () => {
    initAll(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, {
      once: true
    });
  } else {
    boot();
  }

  /*
    Shopify Theme Editor support:
    dynamically added sections initialize independently and unloaded
    sections are fully cleaned up.
  */
  document.addEventListener("shopify:section:load", (event) => {
    const section =
      event.target.matches?.("[data-soap-bubble-reveal]")
        ? event.target
        : event.target.querySelector?.("[data-soap-bubble-reveal]");

    if (section) initSection(section);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const section =
      event.target.matches?.("[data-soap-bubble-reveal]")
        ? event.target
        : event.target.querySelector?.("[data-soap-bubble-reveal]");

    if (!section) return;

    const instance = instances.get(section);

    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  });
})();
